import { useMemo, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import { Label, Typography } from "components/atoms";
import { useAppDispatch } from "hooks";
import { RefreshModal } from "components/molecules/RefreshModal";
import { DrillModal } from "components/organisms/TabClassHomeWork";
import { decodeToken } from "utils/functions/decodeToken";
import { openExternalUrl } from "utils/functions/openExternalUrl";
import "./index.scss";

interface TestData {
  Name: string;
  dueDate: string;
  reason: string[];
  title: string;
}

interface IProps {
  overallProgressData: any;
  theme?: "light" | "dark";
  isFetching?: boolean;
  onRefresh?: () => void;
}

export const GuaranteeEligibilityGridMobile = ({
  overallProgressData,
  theme,
  isFetching,
  onRefresh,
}: IProps) => {
  const modalTheme = theme || "light";
  const dispatch = useAppDispatch();

  const payload = decodeToken();

  const extractEntriesWithStatus = (
    obj: any,
    statuses = ["Not Started", "Not Attended"]
  ) => {
    let result: any = [];

    const recursiveExtract = (data: any) => {
      if (Array.isArray(data)) {
        data.forEach((item: any) => {
          const isValidStatus = item.Status && statuses.includes(item.Status);
          const isInPast = (dateField: any) =>
            dayjs(dateField).isValid()
              ? dayjs(dateField).isBefore(dayjs())
              : false;

          if (isValidStatus) {
            if (
              (item.Status === "Not Started" ||
                item.Status === "Not Attended") &&
              (isInPast(item.DueDate) ||
                isInPast(item.dueDate) ||
                isInPast(item.ClassDate))
            ) {
              result.push(item);
            }
          }
        });
      } else if (typeof data === "object" && data !== null) {
        Object.values(data).forEach((value) => {
          recursiveExtract(value);
        });
      }
    };

    recursiveExtract(obj);
    return result;
  };

  const entriesWithStatus = useMemo(() => {
    return extractEntriesWithStatus(overallProgressData);
  }, [overallProgressData]);

  const data: TestData[] = entriesWithStatus.map((entry: any) => {
    if (entry.hasOwnProperty("DueDate") || entry.hasOwnProperty("dueDate")) {
      const extractDueDate = entry.DueDate || entry.dueDate;
      return {
        name: entry.Name,
        dueDate: dayjs(extractDueDate).isValid()
          ? dayjs.utc(extractDueDate).format("MM/DD/YY")
          : extractDueDate,
        reasons: [entry.Status],
        Status: entry.Status,
        action: "Appeal",
        launchUrl: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.LaunchUrl
          : entry.hasOwnProperty("LaunchUrl")
            ? entry.LaunchUrl
            : "",
        ContentObjectType: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.ContentObjectType
          : entry.Type,
        ContentObjectGuid: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.ContentObjectGuid
          : "",
      };
    } else {
      return {
        name: entry.Name,
        dueDate: dayjs(entry.ClassDate).isValid()
          ? dayjs.utc(entry.ClassDate).format("MM/DD/YY")
          : entry.ClassDate,
        reasons: [entry.Status],
        Status: entry.Status,
        action: "Appeal",
        launchUrl: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.LaunchUrl
          : entry.hasOwnProperty("LaunchUrl")
            ? entry.LaunchUrl
            : "",
        ContentObjectType: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.ContentObjectType
          : entry.Type,
        ContentObjectGuid: entry.hasOwnProperty("ContentObject")
          ? entry.ContentObject.ContentObjectGuid
          : "",
      };
    }
  });

  const [openUpdateModalState, setOpenUpdateModalState] = useState({
    isOpen: false,
    data: null as any,
  });

  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState({
    isOpen: false,
    data: "",
  });

  const handleUpdateModalClose = () => {
    setOpenUpdateModalState((prev: any) => ({
      ...prev,
      isOpen: false,
      data: null,
    }));
  };

  const handleRefreshModalOpen = (item: any) => {
    const contentType = item.ContentObjectType || item.Type || "Test";
    setIsRefreshModalOpen((prev) => ({
      ...prev,
      isOpen: true,
      data: contentType,
    }));
  };

  const handleRefreshModalClose = () => {
    setIsRefreshModalOpen((prev) => ({
      ...prev,
      isOpen: false,
      data: "",
    }));
  };

  const handleRefresh = async () => {
    if (onRefresh) {
      await Promise.resolve(onRefresh());
    }
    handleRefreshModalClose();
  };

  const handleClick = (obj: any) => {
    if (
      obj.ContentObjectType === "OfflineDrill" ||
      obj.ContentObjectType === "OfflineReading"
    ) {
      setOpenUpdateModalState((prev: any) => ({
        ...prev,
        isOpen: true,
        data: obj,
      }));
    } else {
      openExternalUrl({
        url: obj.launchUrl,
        contentObjectType: obj.ContentObjectType || obj.Type,
        testType: payload?.testtype,
        dispatch,
        onRefreshModalOpen: () => {
          handleRefreshModalOpen(obj);
        },
      });
    }
  };

  const getMatchColor = (reason: string) => {
    switch (reason) {
      case "Past Due":
      case "Incompleted":
      case "Not Started":
      case "Not Attended":
        return "red";
      case "Completed":
        return "green";
      default:
        return "gray";
    }
  };

  return (
    <>
      <div className={classNames("guarant-eleigibility-mobile-test-wrapper")}>
        {data &&
          Array.isArray(data) &&
          data.map((item: any, index: number) => {
            const isOffline =
              item.ContentObjectType === "OfflineDrill" ||
              item.ContentObjectType === "OfflineReading";
            const hasLaunchUrl =
              item.launchUrl !== null &&
              item.launchUrl !== undefined &&
              typeof item.launchUrl === "string" &&
              item.launchUrl.length > 0;
            const isClickable = isOffline || hasLaunchUrl;

            return (
              <div
                key={index}
                className={classNames({
                  "guarant-eleigibility-mobile-items": true,
                  highlight: index % 2 === 0,
                })}
              >
                <div onClick={() => (isClickable ? handleClick(item) : null)}>
                  <Typography variant="h8Med" label="Name" />
                  <Typography
                    styles={
                      isClickable
                        ? "guarant-eleigibility-mobile-link-text"
                        : "guarant-eleigibility-mobile-normal-text"
                    }
                    variant="h8Med"
                    label={item.name}
                  />
                </div>
                <div>
                  <Typography variant="h8Med" label="Due Date" />
                  <Typography
                    styles="guarant-eleigibility-mobile-normal-text"
                    variant="h8Book"
                    label={item.dueDate}
                  />
                </div>
                <div>
                  <Typography variant="h8Med" label="Reason(s)" />
                  <div className="reason-wrapper">
                    {item["reasons"].map((value: any, index: number) => (
                      <Label
                        key={index}
                        backgroundColor={getMatchColor(value)}
                        label={value}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
      <RefreshModal
        isOpen={isRefreshModalOpen.isOpen}
        handleOnClose={handleRefreshModalClose}
        theme={modalTheme}
        contentObjectType={
          isRefreshModalOpen.data !== "" ? isRefreshModalOpen.data : "Test"
        }
        isFetching={isFetching}
        handleRefresh={handleRefresh}
      />
      <DrillModal
        handleUpdateModalClose={handleUpdateModalClose}
        openUpdateModalState={openUpdateModalState}
        theme={modalTheme}
        isFetchingOtherAPIs={isFetching}
        refetchData={onRefresh}
      />
    </>
  );
};
